import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import api, { getPublicApiUrl } from '../api';
import BidHistoryModal, { BidItem } from '../components/BidHistoryModal';
import { useTwitchAuth } from '../context/TwitchAuthContext';
import { useSeo } from '../hooks/useSeo';
import { SITE_URL, absoluteUrl, buildTitle, truncateDescription } from '../constants/seo';

interface Auction {
  _id: string;
  title: string;
  description: string;
  imageUrl: string;
  startingPrice: number;
  minimumBid?: number;
  currentHighBid: number;
  minRequiredBid?: number;
  status: string;
  topBidder: { displayName: string; twitchUsername?: string; amount: number } | null;
  closedAt?: string;
  endsAt?: string;
  updatedAt?: string;
}

function getSocketBase() {
  return getPublicApiUrl().replace(/\/api\/?$/, '') || window.location.origin;
}

const AuctionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, token, login, setSession } = useTwitchAuth();
  const [auction, setAuction] = useState<Auction | null>(null);
  const [bids, setBids] = useState<BidItem[]>([]);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAllBids, setShowAllBids] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    const { data } = await api.get(`/auctions/${id}`);
    setAuction(data.auction);
    setBids(data.bids);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useSeo({
    title: buildTitle(auction?.title ?? 'Subasta'),
    description: auction
      ? truncateDescription(auction.description || `Subasta en vivo: ${auction.title}. Pujá con Twitch en EuGe_NiAL.`)
      : 'Subasta en vivo de la comunidad Eugenialitos.',
    image: auction?.imageUrl ? absoluteUrl(auction.imageUrl) : undefined,
    url: id ? `${SITE_URL}/subastas/${id}` : `${SITE_URL}/subastas`,
    type: 'article',
  });

  useEffect(() => {
    if (!id) return;
    const socket: Socket = io(getSocketBase(), { path: '/api/socket.io' });
    socket.emit('join-auction', id);
    socket.on('bid:new', (bid: BidItem) => {
      setBids((prev) => {
        const next = [bid, ...prev.filter((b) => b._id !== bid._id)];
        return next.sort((a, b) => b.amount - a.amount);
      });
      setAuction((prev) => prev ? {
        ...prev,
        currentHighBid: bid.amount,
        topBidder: { displayName: bid.displayName, amount: bid.amount },
      } : prev);
    });
    socket.on('auction:refresh', () => { load(); });
    return () => {
      socket.emit('leave-auction', id);
      socket.disconnect();
    };
  }, [id, load]);

  const placeBid = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !token) {
      login();
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post(`/auctions/${id}/bids`, { amount: parseInt(amount, 10) });
      if (data.sessionToken && user) {
        setSession(data.sessionToken, user);
        delete data.sessionToken;
      }
      setBids((prev) => [data, ...prev.filter((b) => b._id !== data._id)]
        .sort((a, b) => b.amount - a.amount));
      setAuction((prev) => prev ? {
        ...prev,
        currentHighBid: data.amount,
        topBidder: { displayName: data.displayName, amount: data.amount },
      } : prev);
      setAmount('');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'No se pudo registrar la puja');
    } finally {
      setLoading(false);
    }
  };

  if (!auction) {
    return (
      <div className="py-24 px-6 text-center text-slate-400">
        Cargando subasta...
      </div>
    );
  }

  const minBid = auction.minRequiredBid ?? (
    auction.currentHighBid > 0
      ? auction.currentHighBid + (auction.minimumBid || 1)
      : auction.startingPrice
  );

  const previewBids = bids.slice(0, 3);

  return (
    <div className="relative bg-gradient-to-b from-[#0d0a1a] to-[#0a0815] py-12 px-6 min-h-[70vh]">
      <div className="max-w-5xl mx-auto">
        <Link to="/subastas" className="inline-flex text-sm text-slate-400 hover:text-rose-300 mb-6 transition-colors">
          ← Volver a subastas
        </Link>

        <div className="grid lg:grid-cols-[minmax(260px,380px)_1fr] gap-6 items-start">
          <div className="rounded-2xl border border-rose-400/20 bg-gradient-to-br from-rose-400/10 to-pink-400/5 p-5">
            <div className="min-h-[280px] max-h-[360px] flex items-center justify-center bg-black/25 rounded-xl mb-4 overflow-hidden">
              <img src={auction.imageUrl} alt={auction.title} className="max-w-full max-h-[360px] object-contain" />
            </div>
            <h1 className="text-xl font-bold text-white/90 mb-2">{auction.title}</h1>
            {auction.description && (
              <p className="text-sm text-slate-400/80 m-0 whitespace-pre-wrap leading-relaxed">{auction.description}</p>
            )}
            <div className="mt-4 p-4 rounded-xl border border-rose-400/30 bg-rose-400/10 text-sm text-white/90">
              {auction.status === 'closed' ? (
                auction.topBidder ? (
                  <>
                    <strong>Ganador:</strong>{' '}
                    {auction.topBidder.twitchUsername
                      ? `@${auction.topBidder.twitchUsername.replace(/^@/, '')}`
                      : auction.topBidder.displayName}
                    <br />
                    <strong className="text-lg text-rose-200">${auction.topBidder.amount.toLocaleString('es-AR')}</strong>
                    {(auction.closedAt || auction.endsAt || auction.updatedAt) && (
                      <p className="text-xs text-slate-400 mt-2 mb-0">
                        Cerrada el{' '}
                        {new Date(auction.closedAt || auction.endsAt || auction.updatedAt || '').toLocaleString('es-AR', {
                          timeZone: 'America/Argentina/Buenos_Aires',
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    )}
                  </>
                ) : (
                  <>Finalizó sin pujas · base <strong>${auction.startingPrice.toLocaleString('es-AR')}</strong></>
                )
              ) : auction.topBidder ? (
                <>
                  <strong>Puja líder:</strong> {auction.topBidder.displayName}<br />
                  <strong className="text-lg text-rose-200">${auction.topBidder.amount.toLocaleString('es-AR')}</strong>
                </>
              ) : (
                <>Precio base: <strong>${auction.startingPrice.toLocaleString('es-AR')}</strong> — sin pujas aún</>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <h2 className="text-lg font-semibold text-white/90 mt-0 mb-4">
                {auction.status === 'closed' ? 'Resultado' : 'Hacer puja'}
              </h2>
              {auction.status === 'closed' ? (
                <p className="text-sm text-slate-400">
                  Esta subasta ya cerró
                  {auction.topBidder
                    ? `. Se la llevó ${auction.topBidder.twitchUsername
                      ? `@${auction.topBidder.twitchUsername.replace(/^@/, '')}`
                      : auction.topBidder.displayName} por $${auction.topBidder.amount.toLocaleString('es-AR')}.`
                    : ' y no recibió pujas.'}
                </p>
              ) : !user ? (
                <>
                  <p className="text-sm text-slate-400 mb-3">Iniciá sesión con Twitch para pujar.</p>
                  <button
                    type="button"
                    onClick={login}
                    className="px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-rose-400/90 to-pink-500/90 text-white hover:opacity-90"
                  >
                    Entrar con Twitch
                  </button>
                </>
              ) : auction.status === 'paused' ? (
                <p className="text-sm text-slate-400">Esta subasta está pausada. No se aceptan pujas por ahora.</p>
              ) : auction.status !== 'active' ? (
                <p className="text-sm text-slate-400">Esta subasta no está activa.</p>
              ) : (
                <form onSubmit={placeBid} className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1.5">Monto en ARS (mínimo: ${minBid})</label>
                    <input
                      type="number"
                      min={minBid}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      required
                      className="w-full px-4 py-2.5 rounded-xl bg-black/30 border border-white/10 text-white focus:outline-none focus:border-rose-400/50"
                    />
                  </div>
                  {error && <p className="text-sm text-rose-400">{error}</p>}
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-rose-400/90 to-pink-500/90 text-white hover:opacity-90 disabled:opacity-50"
                  >
                    {loading ? 'Enviando...' : 'Pujar'}
                  </button>
                </form>
              )}
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <h2 className="text-lg font-semibold text-white/90 m-0">Historial de pujas</h2>
                {bids.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowAllBids(true)}
                    className="px-3 py-1.5 rounded-xl text-sm border border-white/15 text-slate-300 hover:text-white"
                  >
                    Ver más ({bids.length})
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {previewBids.map((b, i) => (
                  <div
                    key={b._id}
                    className={[
                      'flex justify-between px-3 py-2.5 rounded-xl text-sm',
                      i === 0 ? 'border border-rose-400/40 bg-rose-400/10' : 'border border-white/5',
                    ].join(' ')}
                  >
                    <span>{b.displayName}{b.isManual ? ' (manual)' : ''}</span>
                    <strong>${b.amount}</strong>
                  </div>
                ))}
                {!bids.length && <p className="text-sm text-slate-500 m-0">Todavía no hay pujas.</p>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAllBids && (
        <BidHistoryModal
          title={`Pujas · ${auction.title}`}
          bids={bids}
          onClose={() => setShowAllBids(false)}
        />
      )}
    </div>
  );
};

export default AuctionDetailPage;
